// File: src/domain/BracketSVG.tsx
import React, { useMemo } from "react";
import { Player, Tournament } from "./types";

export type BracketSide = "WB" | "LB" | "GF"; // winners, losers, grand-final

export type Match = {
  id: string;
  round: number; // 1..N within a side
  pos?: number;  // 0..(matchesInRound-1) top→bottom (recommended)
  bracket?: BracketSide; // default WB
  player1Id?: string;
  player2Id?: string;
  winnerId?: string;
  score?: string;
};

// ---------- helpers ----------
const isPow2 = (n: number) => n > 0 && (n & (n - 1)) === 0;
const nextPow2 = (n: number) => { let p = 1; while (p < n) p <<= 1; return p; };
const byIdNatural = <T extends { id: string }>(a: T, b: T) => a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: "base" });

// Try to parse -rX-mY as fallback if pos is missing
function posFromId(id: string): number | undefined {
  const m = id.match(/(?:^|\b|_)-?r(\d+)-m(\d+)(?:\b|$)/i);
  return m ? Number(m[2]) - 1 : undefined; // 0-based
}

export type BracketStyle = {
  colW?: number;   // column width
  rowH?: number;   // box height
  baseGap?: number;// gap between leaves in round 1
  colGap?: number; // horizontal gap between columns
  lineColor?: string;
};

const defaultStyle: Required<BracketStyle> = {
  colW: 260,
  rowH: 56,
  baseGap: 16,
  colGap: 32,
  lineColor: "#CBD5E1", // slate-300
};

/** Build slot arrays for a side. */
function useRoundSlots(matches: Match[], side: BracketSide, sizeHint = 0) {
  const ms = matches.filter(m => (m.bracket ?? "WB") === side);
  const r1 = ms.filter(m => m.round === 1);
  const baseSize = Math.max(2, r1.length * 2);
  const size = isPow2(baseSize) ? Math.max(baseSize, sizeHint) : nextPow2(Math.max(baseSize, sizeHint));
  const roundsCount = Math.log2(size);

  const slots: (Match | null)[][] = [];
  for (let r = 1; r <= roundsCount; r++) {
    const expected = size / 2 ** r;
    const rs = ms.filter(m => m.round === r);
    const withPos = rs.map(m => ({ m, pos: m.pos ?? posFromId(m.id) }));
    const missing = withPos.filter(x => x.pos == null).sort((a,b)=>byIdNatural(a.m,b.m));
    missing.forEach((x,i)=>{ x.pos = i; });
    const arr = Array.from({ length: expected }, () => null as Match | null);
    for (const { m, pos } of withPos) if (pos! >= 0 && pos! < expected) arr[pos!] = m;
    slots.push(arr);
  }
  return { size, roundsCount, slots } as const;
}

/** Compute presence (used for BYE folding & connectors). */
function computePresence(size: number, roundsCount: number, leaves: (Player | null)[], slots: (Match | null)[][]) {
  const presentLeaf = leaves.map(Boolean);
  const presentTop: boolean[][] = Array.from({ length: roundsCount + 1 }, (): boolean[] => []);
  const presentBot: boolean[][] = Array.from({ length: roundsCount + 1 }, (): boolean[] => []);
  const presentOut: boolean[][] = Array.from({ length: roundsCount + 1 }, (): boolean[] => []);

  // Round 1 children presence from leaves
  for (let i = 0; i < size / 2; i++) {
    const a = presentLeaf[i * 2] || false;
    const b = presentLeaf[i * 2 + 1] || false;
    presentTop[1][i] = a; presentBot[1][i] = b; presentOut[1][i] = a || b;
  }
  // Higher rounds from previous round outputs
  for (let r = 2; r <= roundsCount; r++) {
    const count = size / 2 ** r;
    for (let i = 0; i < count; i++) {
      const a = presentOut[r - 1][i * 2] || false;
      const b = presentOut[r - 1][i * 2 + 1] || false;
      presentTop[r][i] = a; presentBot[r][i] = b; presentOut[r][i] = a || b;
    }
  }
  return { presentLeaf, presentTop, presentBot, presentOut } as const;
}

/** Render one single-elimination side using SVG lines + BYE folding. */
function SingleElimSide({
  title,
  tournament,
  matches,
  players,
  side = "WB",
  style: userStyle,
}: {
  title?: string;
  tournament: Tournament;
  matches: Match[];
  players: Player[];
  side?: BracketSide;
  style?: BracketStyle;
}) {
  const S = { ...defaultStyle, ...(userStyle || {}) };
  const playerById = useMemo(() => new Map(players.map(p => [p.id, p])), [players]);

  const { size, roundsCount, slots } = useRoundSlots(matches, side);

  // Build leaves from round-1 matches
  const leaves: (Player | null)[] = useMemo(() => {
    const arr: (Player | null)[] = Array(size).fill(null);
    const r1 = slots[0] || [];
    r1.forEach((m, i) => {
      if (!m) return;
      arr[i * 2] = m.player1Id ? playerById.get(m.player1Id) ?? null : null;
      arr[i * 2 + 1] = m.player2Id ? playerById.get(m.player2Id) ?? null : null;
    });
    return arr;
  }, [slots, size, playerById]);

  const presence = computePresence(size, roundsCount, leaves, slots);

  // Geometry (column x positions & total height)
  const X = (col: number) => col * (S.colW + S.colGap);
  const step = (r: number) => (S.rowH + S.baseGap) * 2 ** r; // center spacing for round r
  const colHeight = (r: number) => step(r) * (size / 2 ** r) - S.baseGap;
  const totalWidth = X(roundsCount) + S.colW;
  const totalHeight = colHeight(0);

  // Boxes to render (leaves + matches)
  const boxes: React.ReactNode[] = [];
  // Round 1 leaves
  for (let i = 0; i < size; i++) {
    if (!presence.presentLeaf[i]) continue; // BYE folding: skip empty leaves
    const top = i * (S.rowH + S.baseGap);
    boxes.push(
      <div key={`leaf-${i}`} style={{ position: "absolute", left: X(0), top, width: S.colW, height: S.rowH }}>
        <div style={{ height: "100%", padding: "8px 10px", border: `1px solid var(--border-subtle)`, borderRadius: 10, background: "var(--surface-elevated)", display: "flex", alignItems: "center", fontWeight: 700 }}>
          {leaves[i]?.name}
        </div>
      </div>
    );
  }
  // Round >=2 boxes: render only when both children present (otherwise it's a BYE pass-through)
  for (let r = 1; r <= roundsCount; r++) {
    const count = size / 2 ** r;
    for (let i = 0; i < count; i++) {
      const centerY = i * step(r) + step(r - 1) + S.rowH / 2;
      const top = centerY - S.rowH / 2;
      const both = presence.presentTop[r][i] && presence.presentBot[r][i];
      if (!both) continue; // BYE folding: skip match box
      const m = slots[r - 1]?.[i] || null;
      const p1 = m?.player1Id ? playerById.get(m!.player1Id) : undefined;
      const p2 = m?.player2Id ? playerById.get(m!.player2Id) : undefined;
      boxes.push(
        <div key={`r${r}-m${i}`} style={{ position: "absolute", left: X(r), top, width: S.colW, height: S.rowH }}>
          <div style={{ height: "100%", padding: "8px 10px", border: `1px solid var(--border-subtle)`, borderRadius: 10, background: "var(--surface-elevated)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ fontWeight: 800 }}>{p1?.name ?? "TBD"}</div>
            <div style={{ fontWeight: 800 }}>{p2?.name ?? "TBD"}</div>
            {!!m?.score && (<div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Score: {m.score}</div>)}
          </div>
        </div>
      );
    }
  }

  // SVG connectors
  const paths: React.ReactNode[] = [];
  const midX = (r: number) => X(r - 1) + S.colW + S.colGap / 2; // center of gap between r-1 and r

  for (let r = 1; r <= roundsCount; r++) {
    const count = size / 2 ** r;
    for (let i = 0; i < count; i++) {
      const cy = i * step(r) + step(r - 1) + S.rowH / 2;
      const childTopY = cy - step(r - 1) / 2;
      const childBotY = cy + step(r - 1) / 2;
      const xLeft = X(r - 1) + S.colW; // right edge of left column boxes
      const xRight = X(r);             // left edge of this round column
      const xm = midX(r);

      const topPresent = presence.presentTop[r][i];
      const botPresent = presence.presentBot[r][i];

      if (topPresent && botPresent) {
        // standard elbow-join shape
        paths.push(<path key={`p-${r}-${i}-top`} d={`M ${xLeft} ${childTopY} H ${xm}`} stroke={S.lineColor} fill="none" strokeWidth={2} />);
        paths.push(<path key={`p-${r}-${i}-bot`} d={`M ${xLeft} ${childBotY} H ${xm}`} stroke={S.lineColor} fill="none" strokeWidth={2} />);
        paths.push(<path key={`p-${r}-${i}-v`} d={`M ${xm} ${childTopY} V ${childBotY}`} stroke={S.lineColor} fill="none" strokeWidth={2} />);
        paths.push(<path key={`p-${r}-${i}-out`} d={`M ${xm} ${cy} H ${xRight}`} stroke={S.lineColor} fill="none" strokeWidth={2} />);
      } else if (topPresent || botPresent) {
        // BYE: straight pass-through from the present child to next column
        const y = topPresent ? childTopY : childBotY;
        paths.push(<path key={`p-${r}-${i}-bye`} d={`M ${xLeft} ${y} H ${xRight}`} stroke={S.lineColor} fill="none" strokeWidth={2} />);
      } else {
        // no participants → draw nothing
      }
    }
  }

  return (
    <div style={{ position: "relative", width: totalWidth, height: totalHeight }}>
      {title && (
        <div style={{ position: "absolute", left: 0, top: -24, color: "var(--text-secondary)", fontWeight: 700, fontSize: 12 }}>{title}</div>
      )}
      {/* Boxes */}
      {boxes}
      {/* SVG overlay for crisp lines */}
      <svg width={totalWidth} height={totalHeight} style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}>
        {paths}
      </svg>
    </div>
  );
}

// ---------- Public components ----------
export function SingleEliminationBracket({
  tournament,
  matches,
  players,
  style,
}: {
  tournament: Tournament;
  matches: Match[];
  players: Player[];
  style?: BracketStyle;
}) {
  return (
    <div>
      <h3 className="card__header">{tournament.name} — {new Date(tournament.date).toLocaleDateString()}</h3>
      <SingleElimSide title="Round 1" tournament={tournament} matches={matches} players={players} side="WB" style={style} />
    </div>
  );
}

export function DoubleEliminationBracket({
  tournament,
  matches,
  players,
  style,
}: {
  tournament: Tournament;
  matches: Match[];
  players: Player[];
  style?: BracketStyle;
}) {
  const S = { ...defaultStyle, ...(style || {}) };
  // winners & losers rendered independently and stacked; GF (size=2) at right aligned to winners mid.
  return (
    <div style={{ position: "relative" }}>
      <h3 className="card__header">{tournament.name} — {new Date(tournament.date).toLocaleDateString()}</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 32 }}>
        <SingleElimSide title="Winners Bracket" tournament={tournament} matches={matches} players={players} side="WB" style={S} />
        <SingleElimSide title="Losers Bracket"   tournament={tournament} matches={matches} players={players} side="LB" style={S} />
        <SingleElimSide title="Grand Final"       tournament={tournament} matches={matches} players={players} side="GF" style={{ ...S, colW: S.colW, baseGap: 16 }} />
      </div>
    </div>
  );
}

export default SingleEliminationBracket;
